import axiosInstance from '../../api/axiosInstance'
import { resolveBoldApiStoreName } from './boldService'

export type VerifyBoldOperationOutcome = 'updated' | 'processed_no_update' | 'skipped_link' | 'skipped_no_tx'

export interface VerifyBoldPaymentOperationParams {
  token: string
  orderId: number
  storeName: string
  paymentId: string
  orderTotal?: string
}

export async function verifyBoldPaymentOperation(
  params: VerifyBoldPaymentOperationParams
): Promise<VerifyBoldOperationOutcome> {
  const { token, orderId, storeName, paymentId: rawPaymentId, orderTotal } = params
  const paymentId = rawPaymentId.trim()
  /** Tienda del pedido en filtros/listado (p. ej. Baristico); API Bold usa bigcenter para Baristico. */
  const boldApiStore = resolveBoldApiStoreName(storeName)
  if (!paymentId || !boldApiStore) {
    return 'skipped_no_tx'
  }

  const storeQuery = encodeURIComponent(boldApiStore)
  let transactionIdForConfirm = paymentId
  const isBoldLinkId = paymentId.toUpperCase().startsWith('LNK_')

  if (isBoldLinkId) {
    const linkIdentifier = encodeURIComponent(paymentId)
    const linkUrl = `/webhook/bold/link/${linkIdentifier}?store_name=${storeQuery}`
    const linkResponse = await axiosInstance.get(linkUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!linkResponse.data?.success || !linkResponse.data?.data) {
      return 'skipped_link'
    }

    const linkData = linkResponse.data.data as {
      id?: string
      transaction_id?: string
      status?: string
      payment_id?: string
      merchant_id?: string
      created_at?: string
      creation_date?: number
      total?: number
      tip?: number
      currency?: string
      taxes?: Array<{ base?: number; type?: string; value?: number }>
      reference?: string
      metadata?: { reference?: string }
      bold_code?: string
      payer?: { email?: string }
      payment_method?:
        | string
        | {
          type?: string
          capture_mode?: string
          card?: {
            brand?: string
            cardholder_name?: string
            card_type?: string
            approval_number?: string
            terminal_id?: string
            masked_pan?: string
            installments?: number
          }
        }
      integration?: string
    }

    const transactionIdFromLink = linkData.transaction_id?.trim?.() || ''
    if (!transactionIdFromLink) {
      return 'skipped_no_tx'
    }

    transactionIdForConfirm = transactionIdFromLink
    const linkStatus = (linkData.status || '').trim().toUpperCase()
    if (linkStatus === 'PAID') {
      const paymentMethodFromLink = typeof linkData.payment_method === 'string'
        ? linkData.payment_method
        : linkData.payment_method?.type || 'CARD'
      const cardFromLink = typeof linkData.payment_method === 'string'
        ? undefined
        : linkData.payment_method?.card
      const paidAtIso = linkData.created_at || new Date().toISOString()
      const webhookTime = linkData.creation_date || Date.now() * 1000000
      const reference = linkData.reference || linkData.metadata?.reference || `ORDER_${orderId}`

      const webhookPayload = {
        id: linkData.id || `${transactionIdFromLink}-${Date.now()}`,
        type: 'SALE_APPROVED',
        subject: linkData.payment_id || transactionIdFromLink,
        source: '/payments',
        spec_version: '1.0',
        time: webhookTime,
        data: {
          payment_id: linkData.payment_id || transactionIdFromLink,
          merchant_id: linkData.merchant_id || boldApiStore,
          created_at: paidAtIso,
          amount: {
            currency: linkData.currency || 'COP',
            total: Number(linkData.total ?? Number(orderTotal) ?? 0),
            taxes: Array.isArray(linkData.taxes) ? linkData.taxes : [],
            tip: Number(linkData.tip ?? 0)
          },
          metadata: {
            reference
          },
          bold_code: linkData.bold_code || linkData.id || paymentId || transactionIdFromLink,
          payer_email: linkData.payer?.email || '',
          payment_method: paymentMethodFromLink,
          card: {
            capture_mode: typeof linkData.payment_method === 'string' ? 'AUTOMATIC' : linkData.payment_method?.capture_mode || 'AUTOMATIC',
            brand: cardFromLink?.brand || '',
            cardholder_name: cardFromLink?.cardholder_name || '',
            terminal_id: cardFromLink?.terminal_id || '',
            masked_pan: cardFromLink?.masked_pan || '',
            installments: Number(cardFromLink?.installments ?? 0),
            card_type: cardFromLink?.card_type || ''
          },
          approval_number: cardFromLink?.approval_number || '',
          integration: linkData.integration || 'PAYMENT_LINK'
        },
        datacontenttype: 'application/json'
      }

      try {
        await axiosInstance.post('/webhook/bold', webhookPayload, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
      } catch (webhookError) {
        console.error('Error enviando webhook adicional de BOLD:', webhookError)
      }
    }
  }

  const voucherIdentifier = encodeURIComponent(transactionIdForConfirm)
  const confirmUrl = `/webhook/bold/voucher/${voucherIdentifier}/confirm?store_name=${storeQuery}`
  const confirmResponse = await axiosInstance.post(
    confirmUrl,
    { id_pedido: orderId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  )

  if (confirmResponse.data?.success && confirmResponse.data?.order_updated) {
    return 'updated'
  }
  return 'processed_no_update'
}